import { useEffect, useCallback, useRef } from 'react';
import config from '../../config';
import { zendeskKey } from '../constants';

export const useLiveChat = () => {
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';
  const scriptLoadedRef = useRef(false);
  const scriptLoadingRef = useRef(false);
  const widgetReadyRef = useRef(false);

  useEffect(() => {
    if (isGS && !scriptLoadedRef.current && !scriptLoadingRef.current) {
      const initializeZendesk = () => {
        // Preconnect to all Zendesk domains for faster subsequent requests
        const domains = [
          'https://static.zdassets.com',
          'https://goldsilver.zendesk.com',
          'https://ekr.zdassets.com',
          'https://widget-mediator.zopim.com',
        ];

        domains.forEach((domain) => {
          const preconnectLink = document.createElement('link');
          preconnectLink.rel = 'preconnect';
          preconnectLink.href = domain;
          preconnectLink.crossOrigin = 'anonymous';
          document.head.appendChild(preconnectLink);
        });

        // DNS prefetch for additional performance
        const dnsPrefetchLink = document.createElement('link');
        dnsPrefetchLink.rel = 'dns-prefetch';
        dnsPrefetchLink.href = '//goldsilver.zendesk.com';
        document.head.appendChild(dnsPrefetchLink);

        // Load the script immediately but asynchronously
        const existingScript = document.querySelector('#ze-snippet');
        if (!existingScript) {
          scriptLoadingRef.current = true;

          const script = document.createElement('script');
          script.id = 'ze-snippet';
          script.type = 'text/javascript';
          script.async = true;
          script.defer = true;
          script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;

          script.onload = () => {
            scriptLoadedRef.current = true;
            scriptLoadingRef.current = false;

            // Pre-initialize the widget for faster opening
            if (window.zE) {
              try {
                // Pre-warm the widget but keep it minimized
                widgetReadyRef.current = true;
              } catch (error) {
                // Silent fail for initialization
              }
            }
          };

          script.onerror = () => {
            scriptLoadingRef.current = false;
          };

          document.body.appendChild(script);
        }
      };

      // Use requestIdleCallback for better performance, fallback to setTimeout
      if (window.requestIdleCallback) {
        window.requestIdleCallback(initializeZendesk, { timeout: 500 });
      } else {
        setTimeout(initializeZendesk, 100);
      }
    }
  }, [isGS]);

  const startLiveChat = useCallback(async () => {
    try {
      if (!isGS) return;

      // If widget is already ready, open immediately
      if (widgetReadyRef.current && window.zE) {
        window.zE('messenger', 'open');
        return;
      }

      // Wait for zE to be available
      const waitForZE = () =>
        new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 30; // 3 seconds max wait

          const checkZE = () => {
            if (window.zE) {
              widgetReadyRef.current = true;
              resolve();
            } else if (attempts < maxAttempts) {
              attempts += 1;
              setTimeout(checkZE, 100);
            } else {
              reject(new Error('Zendesk widget failed to initialize'));
            }
          };

          checkZE();
        });

      await waitForZE();
      window.zE('messenger', 'open');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to start live chat:', error);
    }
  }, [isGS]);

  return { startLiveChat };
};
